import { Button } from '@heidi/design-system/button';
import { TypographyP2SemiBold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { creditUsagePopoverMessages } from './credit-usage-popover-messages';
import { CREDIT_USAGE_POPOVER_PRESETS } from './credit-usage-popover-presets';
import { CreditUsageTriggerIcon } from './credit-usage-trigger-icon';
import type { CreditUsageIndicatorButtonProps } from './types';

/**
 * Tier-icon credit-usage trigger. Compose with PopoverTrigger asChild or wire onClick directly.
 */
export const CreditUsageIndicatorButton = React.forwardRef<
  HTMLButtonElement,
  CreditUsageIndicatorButtonProps
>(function CreditUsageIndicatorButton(
  {
    plan,
    usagePercent,
    triggerVariant = 'tile',
    countdownValue,
    onClick,
    className,
    'aria-label': ariaLabel,
    'data-testid': testId = 'credit-usage-indicator-button',
  },
  ref
) {
  const intl = useIntl();
  const { theme } = CREDIT_USAGE_POPOVER_PRESETS[plan];
  const isPlainTrigger = triggerVariant === 'plain';
  const showCountdown = countdownValue != null;
  const resolvedAriaLabel =
    ariaLabel ??
    intl.formatMessage(creditUsagePopoverMessages.triggerAriaLabel, {
      percent: usagePercent,
    });

  return (
    <Button
      ref={ref}
      type="button"
      variant={isPlainTrigger ? 'secondary' : 'ghost'}
      iconOnly
      size="sm"
      disableAutomaticSvgSizing
      disableAutomaticTypographyClasses={showCountdown}
      className={cn(
        'shrink-0',
        !isPlainTrigger && 'triggerButtonClass' in theme
          ? theme.triggerButtonClass
          : undefined,
        className
      )}
      onClick={onClick}
      aria-label={resolvedAriaLabel}
      data-testid={testId}
    >
      {showCountdown ? (
        <TypographyP2SemiBold className="leading-none text-text-primary-on-dark">
          {countdownValue}
        </TypographyP2SemiBold>
      ) : (
        <CreditUsageTriggerIcon plan={plan} variant={triggerVariant} size={16} />
      )}
    </Button>
  );
});
